import { gradientBorderCss, safeGradient } from "@/shared/utils/gradient-css";
import { isGradient } from "@components/color-picker/lib/detect";
import styled, { css } from "styled-components";
import { resolveBorderWidthCss } from "../utils/border-width";

type ButtonElementProps = {
  $round: string;
  $color: string;
  $colorHover: string;
  $background: string;
  $backgroundHover: string;
  $fontSize: string;
  $horizontalAlignment: string;
  $borderColor: string;
  $borderColorHover: string;
  $borderWidth: string;
  $isBold: boolean;
  $isSecondary?: boolean;
  $textDirection?: string;
};

type CssVarPair = { primary: string; secondary: string };

const CSS_VARS = {
  background: {
    primary: "--button-background-color",
    secondary: "--secondary-button-background-color",
  },
  backgroundHover: {
    primary: "--button-background-hover-color",
    secondary: "--secondary-button-background-hover-color",
  },
  color: {
    primary: "--button-text-color",
    secondary: "--secondary-button-text-color",
  },
  colorHover: {
    primary: "--button-text-hover-color",
    secondary: "--secondary-button-text-hover-color",
  },
  borderColor: {
    primary: "--button-border-color",
    secondary: "--secondary-button-border-color",
  },
  borderColorHover: {
    primary: "--button-border-color-hover",
    secondary: "--secondary-button-border-color-hover",
  },
  round: {
    primary: "--button-border-radius",
    secondary: "--secondary-button-border-radius",
  },
} satisfies Record<string, CssVarPair>;

const pickVar = (pair: CssVarPair, isSecondary?: boolean) =>
  isSecondary ? pair.secondary : pair.primary;

/**
 * Returns `true` for legacy entity override values that should be
 * replaced by the proper CSS variable. Covers:
 *
 *   - `"none"` → legacy stub saved by older flows before secondary
 *     buttons had dedicated hover colors; visually renders as an
 *     unset background.
 *   - `var(--button-*)` references on a secondary button → stale
 *     pointers to the primary CSS variables, written when secondary
 *     buttons inherited from primary. They should now resolve against
 *     the secondary variables.
 */
const isLegacyValue = (value: string, isSecondary?: boolean) => {
  if (!isSecondary || typeof value !== "string") return false;
  // Stale pointers to the primary CSS variables.
  if (value.startsWith("var(--button-")) return true;
  // The `"none"` stub. Older flows wrote it into a secondary hover slot to
  // mean "no dedicated hover", which pinned the slot to a transparent
  // background — so editing the secondary button's hover colors in the design
  // system had no visible effect on those buttons. The sample-slides deck's
  // first button still carries `backgroundHoverSecondary: "none"`.
  //
  // Only secondary slots are remapped. On a primary button `"none"` is a
  // deliberate "no background" and keeps resolving to transparent below.
  if (value === "none") return true;
  return false;
};

const getDesignSystemValue = (
  value: string,
  pair: CssVarPair,
  isSecondary?: boolean,
) => {
  const variable = pickVar(pair, isSecondary);
  if (value === "design-system") return `var(${variable})`;
  // Legacy values are checked BEFORE the `"none"` → transparent shortcut:
  // a secondary slot holding `"none"` has to reach its CSS variable, and the
  // shortcut used to swallow it first.
  if (!value || isLegacyValue(value, isSecondary)) return `var(${variable})`;
  if (value === "none") return "transparent";
  return value;
};

export const ButtonElement = styled.div<ButtonElementProps>`
  &.buttonElement {
    ${({ $textDirection }) =>
      $textDirection === "rtl" &&
      css`
        p,
        span {
          direction: rtl;
        }
      `}

    justify-content: ${({ $horizontalAlignment }) =>
      $horizontalAlignment || "inherit"};

    .buttonWrapper {
      width: 100%;
      line-height: 1 !important;
      transition: all 0.3s ease-in-out;

      background: ${({ $background, $isSecondary }) => {
        const v = getDesignSystemValue(
          $background,
          CSS_VARS.background,
          $isSecondary,
        );
        if (isGradient(v)) {
          const safe = safeGradient(v);
          return safe ?? "transparent";
        }
        return v;
      }};

      border-radius: ${({ $round, $isSecondary }) =>
        getDesignSystemValue($round, CSS_VARS.round, $isSecondary)};

      ${({ $borderColor, $borderWidth, $isSecondary }) => {
        const v = getDesignSystemValue(
          $borderColor,
          CSS_VARS.borderColor,
          $isSecondary,
        );
        const w = resolveBorderWidthCss($borderWidth, $isSecondary);
        return gradientBorderCss(v, w);
      }}

      color: ${({ $color, $isSecondary }) =>
        getDesignSystemValue($color, CSS_VARS.color, $isSecondary)} !important;

      font-weight: ${({ $isBold }) =>
        $isBold ? "bold" : "inherit"} !important;
      font-size: ${({ $fontSize }) =>
        $fontSize ? `${$fontSize}px` : "var(--paragraph-size)"} !important;

      p,
      span {
        line-height: 1;
        transition: all 0.3s ease-in-out;
        color: inherit;
        font: inherit;
      }

      &:hover {
        color: ${({ $colorHover, $isSecondary }) =>
          getDesignSystemValue(
            $colorHover,
            CSS_VARS.colorHover,
            $isSecondary,
          )} !important;

        background: ${({ $backgroundHover, $isSecondary }) => {
          const v = getDesignSystemValue(
            $backgroundHover,
            CSS_VARS.backgroundHover,
            $isSecondary,
          );
          if (isGradient(v)) {
            const safe = safeGradient(v);
            return safe ? `${safe} !important` : "transparent !important";
          }
          return `${v} !important`;
        }};

        ${({ $borderColorHover, $borderWidth, $isSecondary }) => {
          const v = getDesignSystemValue(
            $borderColorHover,
            CSS_VARS.borderColorHover,
            $isSecondary,
          );
          const w = resolveBorderWidthCss($borderWidth, $isSecondary);
          if (isGradient(v)) {
            const safe = safeGradient(v);
            if (!safe) {
              return `border-color: transparent !important; &::before { content: none; }`;
            }
            return `
              border-color: transparent !important;
              &::before {
                content: '';
                position: absolute;
                inset: calc(-1 * ${w});
                border-radius: inherit;
                border: ${w} solid transparent;
                background: ${safe} border-box;
                -webkit-mask: linear-gradient(#000 0 0) padding-box, linear-gradient(#000 0 0);
                -webkit-mask-composite: xor;
                mask: linear-gradient(#000 0 0) padding-box, linear-gradient(#000 0 0);
                mask-composite: exclude;
                pointer-events: none;
              }
            `;
          }
          return `border-color: ${v} !important; &::before { content: none; }`;
        }}

        p,
        span {
          color: inherit;
          font-weight: inherit;
        }
      }

      .resize-controls {
        display: none !important;
      }

      .content {
        text-align: center !important;
      }
    }

    @media screen and (max-width: 768px) {
      justify-content: center;
    }
  }
`;

export const Wrapper = styled.div<{ $align: string }>`
  justify-content: ${({ $align }) => $align || "inherit"};
`;

export const WrapperInner = styled.div<{
  $direction?: string;
  $showSecondaryButton?: string;
  $containerSize?: "fixed" | "auto";
}>`
  grid-template-columns: ${({ $direction, $showSecondaryButton }) =>
    $showSecondaryButton === "two" && ($direction === "row" || !$direction)
      ? "1fr 1fr"
      : "1fr"};
  width: var(--entity-size);

  ${({ $containerSize }) =>
    $containerSize === "auto" &&
    css`
      width: auto;
      .buttonElement {
        width: fit-content;
      }
    `}

  ${({ $showSecondaryButton, $direction, $containerSize }) =>
    handleAutoWidth($showSecondaryButton, $direction, $containerSize)}

  @media screen and (max-width: 768px) {
    width: 100%;
    grid-template-columns: 1fr;
    .buttonElement {
      width: 100%;
    }
  }
`;

const handleAutoWidth = (
  showSecondaryButton: string | undefined,
  direction: string | undefined,
  containerSize: "fixed" | "auto" | undefined,
) => {
  if (containerSize !== "auto") return {};

  const styles: Record<string, string> = {
    "grid-template-columns": "auto",
    "place-items": "center",
  };

  if (showSecondaryButton === "two" && direction === "row") {
    styles["grid-template-columns"] = "auto auto";
  }

  return styles;
};
